import {AfterViewInit, Directive, Input, OnDestroy} from '@angular/core';
import {AbstractControl, NG_VALIDATORS, Validator} from '@angular/forms';
import {TranslateService} from '@ngx-translate/core';
import {GenericFunctionsUtil} from '../utils/generic-functions.util';
import {ValidationElementComponent} from '../components/validation-element/validation-element.component';
import {ValidationRules} from '../models/validation-rules.model';
import {DispatcherService} from '../services/dispatcher.service';
import {ValidationService} from '../services/validation.service';
import {SiValidator} from '../utils/validator';

@Directive({
  selector: '[siValidation]',
  providers: [{provide: NG_VALIDATORS, useExisting: ValidationDirective, multi: true}]
})
export class ValidationDirective implements Validator, AfterViewInit, OnDestroy {
  @Input() siValidationElement: ValidationElementComponent;
  @Input() siValidationExternalErrorText: string;
  @Input() siValidationExternalWarningText: string;
  siValidator = new SiValidator();
  rules: object;
  validationRules: ValidationRules;
  validationRulesKeys: string[];
  observerKey = `validation${Math.random().toString(36).substr(2, 6)}`;
  formControl: AbstractControl;
  afterViewInit = false;
  private _siValidation: string;

  constructor(private translateService: TranslateService,
              private dispatcher: DispatcherService,
              private validationService: ValidationService) {
    this.rules = this.validationService.getRules();
  }

  get siValidation() {
    return this._siValidation;
  }

  @Input()
  set siValidation(val) {
    this._siValidation = val;
    if (this.afterViewInit && (val !== null && val !== undefined)) {
      this.init();
      this.formControl.updateValueAndValidity();
    }
  }

  ngAfterViewInit() {
    if (this.siValidation === null || this.siValidation === undefined) {
      return;
    }

    this.afterViewInit = true;
    this.init();
    this.dispatcher.register('validation-rules-updated', () => this.onValidationRulesUpdated(), this.observerKey);
  }

  init(): void {
    this.validationRules = GenericFunctionsUtil.findInObjectByNestedKey(this.siValidation, this.rules) || {};
    this.validationRulesKeys = Object.keys(this.validationRules);
    this.translateValidationMessages();
    this.manageRequiredWarning();
  }

  onValidationRulesUpdated(): void {
    this.init();
    this.formControl.updateValueAndValidity();
  }

  async translateValidationMessages(): Promise<void> {
    for (let i = 0; i < this.validationRulesKeys.length; i++) {
      const key = this.validationRulesKeys[i];
      this.validationRules[key].message = await this.translateService.get(this.validationRules[key].message).toPromise();
    }
  }

  manageRequiredWarning(): void {
    if (
      this.siValidationElement
      && (
        this.validationRulesKeys.indexOf('notNull') > -1
        || this.validationRulesKeys.indexOf('notNullCheck') > -1
        || this.validationRulesKeys.indexOf('notNullCheckMultiple') > -1
      )
    ) {
      this.siValidationElement.showRequiredElement();
    } else {
      this.siValidationElement.hideRequiredElement();
    }
  }

  validate(control: AbstractControl): { [key: string]: any } | null {
    this.formControl = control;
    if (this.validationRules) {
      const invalidKey = this.validationRulesKeys.find(key => {
        const validatorParams = [control.value].concat(this.validationRules[key].arguments || []);
        return !this.siValidator.use(key, validatorParams);
      });

      if (invalidKey) {
        this.siValidationElement.showWarning(invalidKey, this.validationRules[invalidKey]);
        return {
          [invalidKey]: {value: control.value}
        };
      }
    }

    if (this.siValidationExternalErrorText) {
      this.siValidationElement.showExternalErrorText(this.siValidationExternalErrorText);
      return {
        externalError: {value: control.value}
      };
    }

    if (this.siValidationExternalWarningText) {
      this.siValidationElement.showExternalWarningText(this.siValidationExternalWarningText);
      return null;
    }

    this.siValidationElement.hideWarning();
    return null;
  }

  ngOnDestroy() {
    if (this.siValidation) {
      this.dispatcher.unregisterFunction('validation-rules-updated', this.observerKey);
    }
  }

}
